import React, { useRef, useState, useEffect } from "react";
import { legacyItems } from "../data/mock";
import { Sparkles } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

export const SponsorsSection = () => {
  const [bellRef, bellVisible] = useInView();
  const [legacyRef, legacyVisible] = useInView();
  const [ctaRef, ctaVisible] = useInView();

  return (
    <section id="sponsors">
      {/* CTV — Official Media Sponsor */}
      <div className="mt-16 md:mt-24" style={{ background: "#ffffff" }}>
        {/* Mobile layout — centered, logo-first */}
        <div
          className="block lg:hidden px-6 py-16"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <div className="text-center">
            <img
              src="/ctv_logo.png"
              alt="CTV"
              className="w-64 h-auto mx-auto mb-5"
            />
            <span
              className="inline-block text-[10px] font-medium uppercase tracking-[0.2em] mb-4"
              style={{ fontFamily: "'Inter', Arial, sans-serif", color: "#6b7280" }}
            >
              Official Media Sponsor
            </span>
            <h2
              className="text-[#FE3A80] uppercase leading-[0.85] mb-4"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                fontWeight: 900,
                fontSize: "1.75rem",
              }}
            >
              CTV
            </h2>
            <p
              className="text-[#1f2937] text-sm font-medium leading-relaxed mb-6 mx-auto"
              style={{ fontFamily: "'Inter', Arial, sans-serif", maxWidth: "22rem" }}
            >
              CTV is Canada's most-watched broadcaster and has been #1 for the past 24 months in a row. CTV provides unparalleled entertainment programming across two broadcast networks and streaming from CTV.ca and the CTV app. CTV News is Canada's most-watched news organization both locally and nationally, and has a network of national, international, and local news operations.
            </p>
          </div>
        </div>

        {/* Desktop layout — two column */}
        <div
          ref={bellRef}
          className="hidden lg:block max-w-[87.5rem] mx-auto px-10 py-[120px]"
          style={{ borderTop: "1px solid #e5e7eb" }}
        >
          <span
            className="inline-block text-xs text-[#6b7280] font-medium uppercase tracking-[0.2em] mb-6"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              opacity: bellVisible ? 1 : 0,
              transform: bellVisible ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
            }}
          >
            Official Media Sponsor
          </span>

          <div className="grid grid-cols-2 gap-20 items-center">
            <div
              style={{
                opacity: bellVisible ? 1 : 0,
                transform: bellVisible ? "translateY(0)" : "translateY(40px)",
                transition: "opacity 0.7s ease-out 0.1s, transform 0.7s ease-out 0.1s",
              }}
            >
              <h2
                className="text-[#FE3A80] uppercase leading-[0.85] mb-8"
                style={{
                  fontFamily: "'Montserrat', Arial, sans-serif",
                  fontWeight: 900,
                  fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
                }}
              >
                CTV
              </h2>
              <p
                className="text-[#1f2937] text-lg font-medium leading-relaxed mb-8"
                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
              >
                CTV is Canada's most-watched broadcaster and has been #1 for the past 24 months in a row. CTV provides unparalleled entertainment programming across two broadcast networks and streaming from CTV.ca and the CTV app. CTV News is Canada's most-watched news organization both locally and nationally, and has a network of national, international, and local news operations. Flagship news sites include CTVNews.ca and are complemented by the CTV News apps, which provide a direct connection to Canada's most trusted news anytime and anywhere. With a perspective that is distinctly Canadian, CTV News brings Canadians the international and domestic news stories of the day and is the #1 news organization in Canada.
              </p>
            </div>

            <div
              className="flex items-center justify-center"
              style={{
                opacity: bellVisible ? 1 : 0,
                transform: bellVisible ? "translateY(0)" : "translateY(40px)",
                transition: "opacity 0.7s ease-out 0.3s, transform 0.7s ease-out 0.3s",
              }}
            >
              <img
                src="/ctv_logo.png"
                alt="CTV"
                className="w-full max-w-md h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* === COMMUNITY LEGACY — LIGHT CONTRAST === */}
      <div style={{ background: "#ffffff" }}>
        <div className="max-w-[87.5rem] mx-auto px-5 py-12 md:px-10 md:py-28">
          <div
            ref={legacyRef}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24"
          >
            {/* Left */}
            <div
              style={{
                opacity: legacyVisible ? 1 : 0,
                transform: legacyVisible ? "translateX(0)" : "translateX(-30px)",
                transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
              }}
            >
              <span
                className="inline-block text-[10px] md:text-xs font-medium uppercase tracking-[0.25em] mb-3 md:mb-6"
                style={{ fontFamily: "'Inter', Arial, sans-serif", color: "#9ca3af" }}
              >
                The Future
              </span>
              <h2
                className="uppercase leading-[0.85] mb-4 md:mb-6"
                style={{
                  fontFamily: "'Montserrat', Arial, sans-serif",
                  fontWeight: 900,
                  fontSize: "clamp(1.75rem, 1.2rem + 2vw, 3rem)",
                  color: "#111827",
                }}
              >
                Community <span className="lg:hidden" style={{ color: "#FE3A80" }}>Legacy</span>
                <br className="hidden lg:block" />
                <span className="hidden lg:inline" style={{ color: "#FE3A80" }}>Legacy</span>
              </h2>
              <p
                className="text-sm md:text-lg font-medium leading-relaxed"
                style={{ fontFamily: "'Inter', Arial, sans-serif", color: "#374151", maxWidth: "28rem" }}
              >
                Built to Lead is designed to become an annual platform.
                Early sponsors become founding partners of something that grows every year.
              </p>
            </div>

            {/* Right */}
            <div className="flex flex-col justify-center">
              <ul className="space-y-0">
                {legacyItems.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 md:gap-4 py-3 md:py-5 cursor-default group"
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      opacity: legacyVisible ? 1 : 0,
                      transform: legacyVisible ? "translateX(0)" : "translateX(20px)",
                      transition: `opacity 0.5s ease-out ${0.3 + i * 0.12}s, transform 0.5s ease-out ${0.3 + i * 0.12}s`,
                    }}
                  >
                    <Sparkles size={12} className="text-[#FE3A80] flex-shrink-0 transition-transform duration-300 group-hover:scale-125 md:w-[14px] md:h-[14px]" />
                    <span
                      className="text-sm md:text-base font-medium transition-colors duration-300 group-hover:text-[#FE3A80]"
                      style={{ fontFamily: "'Inter', Arial, sans-serif", color: "#1f2937" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* === FINAL CTA — DARK BLOCK === */}
      <div
        id="register"
        className="relative w-screen overflow-hidden"
        style={{
          marginLeft: "calc(-50vw + 50%)",
          background: "radial-gradient(ellipse at 50% 80%, #1a1a2e 0%, #0f0f1a 60%, #0a0a12 100%)",
        }}
      >
        <div
          ref={ctaRef}
          className="max-w-[87.5rem] mx-auto px-5 py-12 md:px-10 md:py-28 text-center"
        >
          <span
            className="inline-block text-[10px] md:text-xs font-medium uppercase tracking-[0.25em] mb-4 md:mb-8"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              color: "#fca5c3",
              opacity: ctaVisible ? 1 : 0,
              transition: "opacity 0.6s ease-out",
            }}
          >
            The Opportunity
          </span>
          <h2
            className="uppercase leading-[0.85] mb-5 md:mb-8 mx-auto"
            style={{
              fontFamily: "'Montserrat', Arial, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(1.5rem, 1rem + 2.5vw, 3.75rem)",
              maxWidth: "18ch",
              color: "#ffffff",
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.8s ease-out 0.1s, transform 0.8s ease-out 0.1s",
            }}
          >
            Be Seen.<br />Be Heard.<br />
            <span style={{ color: "#fca5c3" }}>Be Remembered.</span>
          </h2>
          <p
            className="text-sm md:text-lg font-medium leading-relaxed max-w-2xl mx-auto mb-3 md:mb-4"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              color: "rgba(255,255,255,0.7)",
              opacity: ctaVisible ? 1 : 0,
              transition: "opacity 0.6s ease-out 0.3s",
            }}
          >
            We are inviting select partners to stand with us in building confidence, leadership, and opportunity for the next generation.
          </p>
          <p
            className="text-xs md:text-base font-medium leading-relaxed max-w-xl mx-auto mb-8 md:mb-14"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              color: "rgba(255,255,255,0.4)",
              opacity: ctaVisible ? 1 : 0,
              transition: "opacity 0.6s ease-out 0.4s",
            }}
          >
            We welcome the chance to build something meaningful together.
          </p>
          <div
            className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4"
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? "translateY(0)" : "translateY(15px)",
              transition: "opacity 0.6s ease-out 0.5s, transform 0.6s ease-out 0.5s",
            }}
          >
            <a
              href="#contact"
              className="inline-flex items-center justify-center border-none px-8 py-3.5 md:px-10 md:py-4 text-sm font-semibold uppercase cursor-pointer transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] no-underline"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                letterSpacing: "0.1em",
                background: "#FE3A80",
                color: "#ffffff",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#ff5a96"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#FE3A80"; }}
            >
              Partner With Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
